import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetChildFeedQuery } from '../store/api/localApi';
import { FeedItem } from '../types';

/**
 * One child's feed (moments + observations, newest first), accumulated across
 * every cursor page fetched so far. We accumulate pages locally rather than
 * replacing, because RTK Query keys each cursor as a distinct cache entry.
 * `loadMore` advances to the next page; the host decides when (e.g. an
 * IntersectionObserver sentinel).
 */
export function useChildFeedPages(childId: string) {
  // The cursor we're currently fetching. `undefined` = first page.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // Accumulated items across every page fetched so far, plus the set of cursors
  // we've already merged (so a re-render can't double-append the same page).
  const [items, setItems] = useState<FeedItem[]>([]);
  // The next cursor from the last page merged. Held in state rather than read
  // off the query so "there is more" stays true while that next page loads.
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const mergedCursors = useRef<Set<string>>(new Set());

  // Reset when the child changes — a different kid is a different feed.
  useEffect(() => {
    setCursor(undefined);
    setItems([]);
    setNextCursor(undefined);
    mergedCursors.current = new Set();
  }, [childId]);

  // `currentData`, never `data`: RTK keeps the previous arg's `data` while a new
  // arg loads, so merging `data` would file one child's (or one cursor's) page
  // under another's key — showing the old child's items and then skipping the
  // new child's real first page as already merged.
  const { currentData, isFetching, isLoading } = useGetChildFeedQuery({ childId, cursor });

  // Merge each freshly-fetched page in exactly once. Keyed by the cursor that
  // produced it ('' for the first page) so React 18's double-invoke or a
  // re-render never appends the same page twice.
  useEffect(() => {
    if (!currentData) return;
    const key = cursor ?? '';
    if (mergedCursors.current.has(key)) return;
    mergedCursors.current.add(key);
    setItems((prev) => [...prev, ...currentData.items]);
    setNextCursor(currentData.nextCursor);
  }, [currentData, cursor]);

  const loadMore = useCallback(() => {
    if (nextCursor) setCursor(nextCursor);
  }, [nextCursor]);

  return { items, nextCursor, isFetching, isLoading, loadMore };
}
